import type { ServiceType } from "../../data/types";
import type { CaseKind } from "../../data/workspace";
export const servicePaths: Record<ServiceType, string> = {
  balloon: "/vuelos",
  tour: "/tours",
  school: "/grupos-escolares",
};
/** Readable URL values for the funnel service filter (/embudo?servicio=vuelos). */
export const serviceSlugs: Record<ServiceType, string> = {
  balloon: "vuelos",
  tour: "tours",
  school: "escuelas",
};
export function serviceFromSlug(slug: string | null): ServiceType | undefined {
  return (Object.keys(serviceSlugs) as ServiceType[]).find(
    (service) => serviceSlugs[service] === slug,
  );
}
export function funnelUrl(
  service?: ServiceType | null,
  view: "ventas" | "reservas" = "ventas",
  extra: Record<string, string> = {},
) {
  const params = new URLSearchParams();
  if (service) params.set("servicio", serviceSlugs[service]);
  if (view !== "ventas") params.set("vista", view);
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  const search = params.toString();
  return search ? `/embudo?${search}` : "/embudo";
}
export const serviceNames: Record<ServiceType, string> = {
  balloon: "Vuelos en globo",
  tour: "Tours",
  school: "Grupos escolares",
};
export const stageNames: Record<string, string> = {
  new: "Nuevo",
  conversation: "En conversación",
  quote: "Cotización enviada",
  awaiting: "Esperando anticipo",
  won: "Apartado",
  done: "Realizado",
  lost: "Perdido",
  pending: "Pendiente",
  confirmed: "Confirmada",
  completed: "Realizado",
  cancelled: "Cancelada",
  paid: "Pagado",
  unpaid: "Sin pago recibido",
  partially_paid: "Pago parcial",
  overdue: "Pago vencido",
  on_hold: "En espera",
  "on-hold": "En espera",
  rejected: "Rechazado",
  queued: "Pendiente de envío",
  resend_requested: "Reenvío solicitado",
  partial: "Anticipo recibido",
  deposit_paid: "Anticipo recibido",
  stripe_prepared: "Pago sin completar",
  payment_abandoned: "Intento de pago abandonado",
  payment_failed: "Pago no procesado",
  payment_pending: "Pendiente de pago",
  pending_payment: "Pendiente de pago",
  holding: "Apartado temporal",
  partially_refunded: "Reembolso parcial",
  adult: "Adulto",
  child: "Niño",
  infant: "Infante",
  accepted_by_mailer: "Aceptado por correo",
  sent: "Enviado",
  delivered: "Entregado",
  manual: "Manual",
  web: "Web",
  open: "Abierta",
  refunded: "Reembolsado",
  failed: "Falló",
  contacted: "En seguimiento",
  qualified: "Calificada",
  held: "Apartado temporal",
  processing: "En proceso",
  draft: "Borrador",
  issued: "Emitida",
  accepted: "Aceptada",
  converted: "Convertida",
  expired: "Vencida",
};
export function caseUrl(kind: CaseKind, id: string, returnTo: string) {
  return `/casos/${kind}/${encodeURIComponent(id)}?return=${encodeURIComponent(returnTo)}`;
}
export function safeReturn(value: string | null, fallback = "/") {
  return value?.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("\\")
    ? value
    : fallback;
}
export function money(
  value: number | string | null | undefined,
  currency = "MXN",
) {
  return value == null
    ? "Por definir"
    : new Intl.NumberFormat("es-MX", { style: "currency", currency }).format(
        Number(value),
      );
}
export function copyLabel(value: unknown) {
  return typeof value === "string"
    ? (stageNames[value] ?? value)
    : "Sin registrar";
}

/** Payment attempts older than this (48 h) are abandoned: they leave Hoy and wait in Por cobrar. */
export const STALE_ATTEMPT_MS = 48 * 3600 * 1000;
/** A payment or booking attempt nobody completed for 48 hours (a reported transfer never is). */
export function isStaleAttempt(item: { kind: string; createdAt: string; transferReported?: boolean }, now = Date.now()) {
  if (item.kind !== "payment" && item.kind !== "booking") return false;
  const created = Date.parse(item.createdAt);
  return !item.transferReported && Number.isFinite(created) && created < now - STALE_ATTEMPT_MS;
}
